// Financials tab redesign — the "Phoenix Income Pools" booked ledger: one row
// per period (UTC month), Phoenix-retained income pivoted across the revenue
// pools. A read-time projection over recognized IncomeEvents (owner ruling
// D-18 — the events ARE the booking; nothing here is editable). Pool G
// ("Phoenix Interest & Service Income") is a real pool row and IS the
// non-commissionable bucket — rendered visibly labeled as such off the
// isCommissionable flag. Rows drill into the period's underlying events.

import { useMemo } from "react";
import { Button, Card, EmptyState, Skeleton } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { useLoanIncomePools } from "@/hooks/portfolioAdmin/useIncomePools";
import type {
  IncomePoolColumnDto,
  IncomePoolsPeriodRowDto,
} from "@/api/portfolioAdmin/incomePoolsApi";
import { SCHEDULE_PAGE_SIZE } from "./scheduleTables";

/** "July 2026" for a month-bucket key — formatted in UTC so the first-of-month
 *  UTC key never rolls back a month in a western timezone. */
export function formatPoolPeriod(periodStart: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(periodStart));
}

function cellAmount(row: IncomePoolsPeriodRowDto, poolCode: string | null): number {
  return row.pools.find((c) => c.poolCode === poolCode)?.amount ?? 0;
}

function money(value: number): string {
  return formatCurrency(value, { maximumFractionDigits: 2 });
}

export function IncomePoolsPanel({
  loanId,
  onOpenPeriod,
}: {
  loanId: string;
  /** Fires with the clicked row's periodStart (month-bucket key) — opens the
   *  period's underlying-events drawer. */
  onOpenPeriod: (periodStart: string) => void;
}) {
  const query = useLoanIncomePools(loanId);
  const dto = query.data;

  // Column set: every non-retired pool always renders; a retired pool (C/H)
  // only when it actually holds money; the "unattributed" bucket only when a
  // fail-soft recognition left events pool-less.
  const visiblePools = useMemo<IncomePoolColumnDto[]>(() => {
    if (!dto) return [];
    return dto.pools.filter(
      (p) =>
        !p.isRetired ||
        dto.periods.some((row) => cellAmount(row, p.code) !== 0),
    );
  }, [dto]);
  const hasUnattributed = useMemo(
    () => !!dto && dto.periods.some((row) => row.pools.some((c) => c.poolCode === null)),
    [dto],
  );

  const columns = useMemo<Column<IncomePoolsPeriodRowDto>[]>(() => {
    const poolColumns: Column<IncomePoolsPeriodRowDto>[] = visiblePools.map((p) => ({
      key: `pool-${p.code}`,
      header: p.isCommissionable ? p.code : `${p.code} · non-commissionable`,
      className: "text-right tabular-nums",
      render: (row) => {
        const amount = cellAmount(row, p.code);
        return amount !== 0 ? money(amount) : "—";
      },
    }));
    return [
      {
        key: "period",
        header: "Period",
        render: (row) => formatPoolPeriod(row.periodStart),
      },
      ...poolColumns,
      ...(hasUnattributed
        ? [
            {
              key: "pool-unattributed",
              header: "Unattributed",
              className: "text-right tabular-nums",
              render: (row: IncomePoolsPeriodRowDto) => {
                const amount = cellAmount(row, null);
                return amount !== 0 ? money(amount) : "—";
              },
            },
          ]
        : []),
      {
        key: "total",
        header: "Total",
        className: "text-right tabular-nums font-semibold",
        render: (row) => money(row.total),
      },
    ];
  }, [visiblePools, hasUnattributed]);

  if (query.isLoading) {
    return (
      <Card data-testid="income-pools-loading">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-5 w-full mt-space-3" />
        <Skeleton className="h-5 w-full mt-space-2" />
      </Card>
    );
  }

  if (query.isError || !dto) {
    return (
      <Card data-testid="income-pools-error">
        <p className="text-small text-status-error m-0 mb-space-2">
          Phoenix Income Pools could not be loaded.
        </p>
        <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
          Retry
        </Button>
      </Card>
    );
  }

  return (
    <Card data-testid="income-pools-panel">
      <h3 className="font-display font-bold text-h3 m-0 mb-space-1">
        Phoenix Income Pools
      </h3>
      <p className="text-small text-gray-mid m-0 mb-space-3">
        Booked Phoenix income per period, by revenue pool — recognized as each
        period&apos;s money settles (distribution approval on TPS-serviced loans,
        payment processing otherwise, invoice payment for vendor fee income).
        Most recent period first; view-only.
      </p>
      {dto.periods.length === 0 ? (
        <EmptyState
          title="Nothing booked yet"
          description="Pool income books as the loan's payments settle. No period has booked Phoenix income so far."
        />
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={dto.periods}
            rowKey={(row) => row.periodStart}
            pagination={{ pageSize: SCHEDULE_PAGE_SIZE }}
            onRowClick={(row) => onOpenPeriod(row.periodStart)}
            rowTestId={(row) => `pool-period-${row.periodStart.slice(0, 7)}`}
            data-testid="income-pools-table"
          />
          <dl className="m-0 mt-space-3 text-caption text-gray-mid" data-testid="income-pools-legend">
            {visiblePools.map((p) => (
              <div key={p.code} className="flex gap-space-2">
                <dt className="m-0 font-semibold">{p.code}</dt>
                <dd className="m-0">
                  {p.name}
                  {!p.isCommissionable && " — non-commissionable"}
                  {p.isRetired && " (retired)"}
                </dd>
              </div>
            ))}
          </dl>
        </>
      )}
    </Card>
  );
}
